import React, { Component } from "react";

import {
  RiArrowLeftLine,
  RiCloseFill,
  RiRefreshLine,
  RiSearchLine
} from "react-icons/ri";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { bindActionCreators } from "redux";

import { showHelpdesk } from "../../actions/noteActions";
import URL from "../../config/api";
import axios from "../../config/axios";
import "./style.scss";

/* Minúsculas e sem acento, para comparar texto digitado com o conteúdo. */
const normalizar = texto =>
  String(texto || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/* Este componente não usa nada do CoreUI de propósito — só HTML puro e os
   ícones do react-icons, que o header já usava antes.
   O widget fica montado sobre todas as telas do AVA, então qualquer
   componente de terceiro aqui dentro (tooltip, modal, popper) vira risco de
   travar a plataforma inteira. Botão é <button> com title; o "carregando" é
   um span com animação em CSS. Nada aqui pode prender o clique de outra tela.

   A árvore inteira vem de uma vez em helpdesk/faqTheme/tree — só os nomes dos
   nós e os títulos das dúvidas, sem as respostas, então o peso é pequeno. Em
   troca, a navegação entre os níveis é instantânea. A resposta de cada dúvida
   é buscada sob demanda em helpdesk/faq/singleView/{id} e fica em memória.

   O widget não decide quem vê o quê. A árvore já chega recortada pelo perfil
   ativo (FaqVisibilidadeService, no backend), e o widget só desenha o que
   recebeu — por isso "Início", busca e troca de área não têm como mostrar
   conteúdo de outro perfil: ele nem chega ao navegador. Não acrescente
   filtro por perfil aqui; seria uma segunda regra para manter, e a do
   cliente, defasada, esconderia o que é permitido.

   MERGE COM ALFA: a branch alfa tem outra versão deste widget (commits de
   21/09/2026). Num conflito, mantenha esta. */

class FaqChatWidget extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      loadError: false,
      tree: [],
      /* Caminho percorrido na árvore, da área até o nó atual. Vazio = o
         usuário está escolhendo a área. */
      path: [],
      selectedQuestion: null,
      answer: null,
      loadingAnswer: false,
      answerCache: {},
      /* A busca é um atalho, não o caminho principal: fica recolhida atrás
         do ícone de lupa para não competir com a navegação por cliques. */
      searchOpen: false,
      search: ""
    };
  }

  componentDidMount() {
    this.montado = true;
    this.loadTree();
  }

  /* Quem tem mais de um perfil troca de papel na sessão: a árvore e as
     respostas guardadas eram do perfil anterior e precisam sair. */
  componentDidUpdate(prevProps) {
    if (this.activeRoleId(prevProps) !== this.activeRoleId(this.props)) {
      this.loadTree();
    }
  }

  /* Evita setState depois que o usuário fecha o widget no meio de uma
     requisição — é aviso no console e, em cadeia, comportamento estranho. */
  componentWillUnmount() {
    this.montado = false;
  }

  setSafeState(patch) {
    if (this.montado) {
      this.setState(patch);
    }
  }

  activeRoleId(props) {
    const geral = props.general || {};

    return (geral.active_roles || {}).role_id;
  }

  /* O perfil ativo vai em toda chamada: o backend confere que o usuário tem
     esse perfil e recorta a resposta por ele. */
  authHeaders() {
    return {
      headers: { authorization: `Bearer ${this.props.user.token}` },
      params: { role_id: this.activeRoleId(this.props) }
    };
  }

  loadTree() {
    this.setState({ loading: true, loadError: false, answerCache: {} });

    axios
      .get(`${URL.api}helpdesk/faqTheme/tree`, this.authHeaders())
      .then(res => {
        const tree = (res && res.data && res.data.data) || [];

        this.setSafeState({
          tree,
          loading: false,
          loadError: false,
          path: this.defaultPath(tree),
          selectedQuestion: null,
          answer: null,
          answerCache: {},
          searchOpen: false,
          search: ""
        });
      })
      .catch(() => {
        toastr.error(
          "Falha",
          "Não foi possível carregar as dúvidas frequentes."
        );
        this.setSafeState({ loading: false, loadError: true, tree: [] });
      });
  }

  /* Abre na área marcada como inicial do perfil (`home`, definida pelo
     backend). Sem uma, e com uma área só, abre nela; com várias, mostra a
     escolha — é o caso do Administrador Educandus, que não tem área própria. */
  defaultPath(tree) {
    if (!Array.isArray(tree) || !tree.length) {
      return [];
    }

    const inicial = tree.find(node => node.home);

    if (inicial) {
      return [inicial];
    }

    return tree.length === 1 ? [tree[0]] : [];
  }

  /* "Início" e "outro perfil" levam à lista de áreas — que, com uma área só,
     é a mesma tela de onde a pessoa veio. */
  hasManyAreas() {
    return this.state.tree.length > 1;
  }

  currentNode() {
    const { path } = this.state;

    return path.length ? path[path.length - 1] : null;
  }

  openNode(node) {
    this.setState(prev => ({
      path: prev.path.concat([node]),
      selectedQuestion: null,
      answer: null,
      search: ""
    }));
  }

  /* Volta um nível. Estando numa resposta, volta para a lista de dúvidas. */
  goBack() {
    if (this.state.selectedQuestion) {
      this.setState({ selectedQuestion: null, answer: null });
      return;
    }

    this.setState(prev => ({
      path: prev.path.slice(0, -1),
      search: ""
    }));
  }

  /* Volta para um ponto específico da trilha (clique no breadcrumb). */
  goToDepth(depth) {
    this.setState(prev => ({
      path: prev.path.slice(0, depth),
      selectedQuestion: null,
      answer: null,
      search: ""
    }));
  }

  changeProfile() {
    this.setState({
      path: [],
      selectedQuestion: null,
      answer: null,
      search: ""
    });
  }

  toggleSearch() {
    this.setState(prev => ({
      searchOpen: !prev.searchOpen,
      search: prev.searchOpen ? "" : prev.search
    }));
  }

  /* Abre o mesmo modal de chamado do botão do header. Não é redirecionamento:
     o modal sobe por cima da tela atual e a captura de tela vai junto com o
     chamado — com o chat aberto, o que já mostra ao suporte o que a pessoa
     estava procurando. O widget continua aberto atrás. */
  abrirChamado() {
    this.props.showHelpdesk();
  }

  renderAbrirChamado(texto) {
    return (
      <button
        type="button"
        className="faq-chat-widget__ticket"
        onClick={() => this.abrirChamado()}
      >
        {texto}
      </button>
    );
  }

  selectQuestion(question) {
    const cached = this.state.answerCache[question.id];

    if (cached) {
      this.setState({ selectedQuestion: question, answer: cached });
      return;
    }

    this.setState({
      selectedQuestion: question,
      answer: null,
      loadingAnswer: true
    });

    axios
      .get(
        `${URL.api}helpdesk/faq/singleView/${question.id}`,
        this.authHeaders()
      )
      .then(res => {
        const answer = (res && res.data && res.data.data) || null;

        this.setSafeState(prev => ({
          answer,
          loadingAnswer: false,
          answerCache: { ...prev.answerCache, [question.id]: answer }
        }));
      })
      .catch(() => {
        toastr.error(
          "Falha",
          "Não foi possível carregar a resposta dessa dúvida."
        );
        this.setSafeState({ loadingAnswer: false, selectedQuestion: null });
      });
  }

  /* Busca por texto dentro da área atual. Como a árvore inteira já está em
     memória, o filtro é local — sem ida ao servidor a cada tecla.

     Compara sem acento dos dois lados: em português quase ninguém digita
     "dúvida" ou "avaliação" com acento numa caixa de busca, e sem isto a
     pessoa conclui que o FAQ não tem a resposta. */
  searchResults() {
    const termo = normalizar(this.state.search);

    if (termo.length < 3) {
      return [];
    }

    const raiz = this.state.path.length ? this.state.path[0] : null;
    const escopo = raiz ? [raiz] : this.state.tree;
    const encontrados = [];

    const percorrer = (nodes, trilha) => {
      nodes.forEach(node => {
        const trilhaNo = trilha.concat([node.name]);

        (node.faqs || []).forEach(faq => {
          if (normalizar(faq.question).indexOf(termo) !== -1) {
            encontrados.push({ faq, trilha: trilhaNo.join(" › ") });
          }
        });

        percorrer(node.children || [], trilhaNo);
      });
    };

    percorrer(escopo, []);

    return encontrados;
  }

  renderSpinner() {
    return (
      <div className="faq-chat-widget__loading">
        <span className="faq-chat-widget__spinner" />
      </div>
    );
  }

  renderBreadcrumb() {
    const { path } = this.state;

    if (!path.length) {
      return null;
    }

    const comInicio = this.hasManyAreas();

    return (
      <div className="faq-chat-widget__breadcrumb">
        {comInicio && (
          <button
            type="button"
            className="faq-chat-widget__crumb"
            onClick={() => this.changeProfile()}
          >
            Início
          </button>
        )}
        {path.map((node, index) => (
          <React.Fragment key={node.id}>
            {(comInicio || index > 0) && (
              <span className="faq-chat-widget__crumb-sep">›</span>
            )}
            <button
              type="button"
              className="faq-chat-widget__crumb"
              onClick={() => this.goToDepth(index + 1)}
              disabled={
                index === path.length - 1 && !this.state.selectedQuestion
              }
            >
              {node.name}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  }

  /* Sem isto, falha de carregamento e árvore vazia caem na mesma tela: a
     saudação sem nenhum botão embaixo — que parece a tela travada, e não dá
     pista do que aconteceu. */
  renderEmptyState() {
    const { loadError } = this.state;

    return (
      <div className="faq-chat-widget__step">
        <div className="faq-chat-widget__bubble faq-chat-widget__bubble--bot">
          {loadError
            ? "Não consegui carregar as dúvidas frequentes agora. Verifique sua conexão e tente novamente."
            : "Ainda não há dúvidas cadastradas para o seu perfil."}
        </div>
        {loadError && (
          <button
            type="button"
            className="faq-chat-widget__link"
            onClick={() => this.loadTree()}
          >
            ↺ Tentar novamente
          </button>
        )}
        <div className="faq-chat-widget__bubble faq-chat-widget__bubble--bot">
          Se preferir, fale direto com o suporte:
        </div>
        {this.renderAbrirChamado("Abrir chamado")}
      </div>
    );
  }

  renderAreas() {
    const { tree } = this.state;

    if (!tree.length) {
      return this.renderEmptyState();
    }

    return (
      <div className="faq-chat-widget__step">
        <div className="faq-chat-widget__bubble faq-chat-widget__bubble--bot">
          Olá! 👋 Seja bem-vindo(a) ao suporte do AVA.
          <br />
          Para começar, sobre qual perfil você quer tirar dúvidas?
        </div>
        <div className="faq-chat-widget__options">
          {tree.map(area => (
            <button
              key={area.id}
              type="button"
              className="faq-chat-widget__option-card"
              onClick={() => this.openNode(area)}
            >
              <span className="faq-chat-widget__option-title">{area.name}</span>
              {area.description && (
                <span className="faq-chat-widget__option-desc">
                  {area.description}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderNode() {
    const node = this.currentNode();
    const children = node.children || [];
    const faqs = node.faqs || [];
    const ehArea = this.state.path.length === 1;

    return (
      <div className="faq-chat-widget__step">
        <div className="faq-chat-widget__bubble faq-chat-widget__bubble--bot">
          {ehArea
            ? `Estas são as áreas de dúvida de ${node.name}. Sobre qual delas você precisa de ajuda?`
            : `Sobre "${node.name}", escolha o que você precisa saber:`}
        </div>

        {/* Quando o nó tem os dois tipos de opção, as etiquetas deixam claro
            o que ainda abre outro nível e o que já é a dúvida final. */}
        {children.length > 0 && (
          <div className="faq-chat-widget__options">
            {faqs.length > 0 && (
              <span className="faq-chat-widget__group-label">
                Escolha um assunto
              </span>
            )}
            {children.map(child => (
              <button
                key={`t-${child.id}`}
                type="button"
                className="faq-chat-widget__option-card"
                onClick={() => this.openNode(child)}
              >
                <span className="faq-chat-widget__option-title">
                  {child.name}
                </span>
                {child.description && (
                  <span className="faq-chat-widget__option-desc">
                    {child.description}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {faqs.length > 0 && (
          <div className="faq-chat-widget__options">
            {children.length > 0 && (
              <span className="faq-chat-widget__group-label">
                Ou vá direto a uma dúvida
              </span>
            )}
            {faqs.map(faq => (
              <button
                key={`q-${faq.id}`}
                type="button"
                className="faq-chat-widget__option-card faq-chat-widget__option-card--question"
                onClick={() => this.selectQuestion(faq)}
              >
                {faq.question}
              </button>
            ))}
          </div>
        )}

        {ehArea && this.hasManyAreas() && (
          <button
            type="button"
            className="faq-chat-widget__link"
            onClick={() => this.changeProfile()}
          >
            Não é esse o seu perfil? Ver dúvidas de outro perfil
          </button>
        )}
      </div>
    );
  }

  renderAnswer() {
    const { selectedQuestion, answer, loadingAnswer } = this.state;

    return (
      <div className="faq-chat-widget__step">
        <div className="faq-chat-widget__bubble faq-chat-widget__bubble--user">
          {selectedQuestion.question}
        </div>

        {loadingAnswer && this.renderSpinner()}

        {!loadingAnswer && answer && (
          <div className="faq-chat-widget__resolution">
            {answer.answer}

            {answer.navigation_path && (
              <div className="faq-chat-widget__path">
                <strong>Onde fica:</strong> {answer.navigation_path}
              </div>
            )}

            {answer.action_url && (
              <a
                href={answer.action_url}
                target="_blank"
                rel="noopener noreferrer"
                className="faq-chat-widget__action"
              >
                {answer.action_label || "Acessar direcionamento"}
              </a>
            )}
          </div>
        )}

        {!loadingAnswer && answer && (
          <div className="faq-chat-widget__followup">
            Isso resolveu? {this.renderAbrirChamado("Não — abrir chamado")}
          </div>
        )}

        <button
          type="button"
          className="faq-chat-widget__link"
          onClick={() => this.goBack()}
        >
          ‹ Ver outras dúvidas
        </button>
      </div>
    );
  }

  renderSearch() {
    const resultados = this.searchResults();
    const termo = this.state.search.trim();

    if (termo.length < 3) {
      return (
        <div className="faq-chat-widget__step">
          <div className="faq-chat-widget__bubble faq-chat-widget__bubble--bot">
            Digite ao menos 3 letras para buscar.
          </div>
        </div>
      );
    }

    if (!resultados.length) {
      return (
        <div className="faq-chat-widget__step">
          <div className="faq-chat-widget__bubble faq-chat-widget__bubble--bot">
            Não encontrei nenhuma dúvida com &quot;{termo}&quot;. Tente outra
            palavra — ou registre um chamado, que a equipe de suporte responde.
          </div>
          {this.renderAbrirChamado("Abrir chamado sobre isso")}
        </div>
      );
    }

    return (
      <div className="faq-chat-widget__step">
        <div className="faq-chat-widget__bubble faq-chat-widget__bubble--bot">
          {resultados.length === 1
            ? "Encontrei 1 dúvida:"
            : `Encontrei ${resultados.length} dúvidas:`}
        </div>
        <div className="faq-chat-widget__options">
          {resultados.map(item => (
            <button
              key={`s-${item.faq.id}`}
              type="button"
              className="faq-chat-widget__option-card faq-chat-widget__option-card--question"
              onClick={() => this.selectQuestion(item.faq)}
            >
              <span className="faq-chat-widget__option-title">
                {item.faq.question}
              </span>
              <span className="faq-chat-widget__option-desc">
                {item.trilha}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderBody() {
    const { loading, selectedQuestion, search, searchOpen } = this.state;

    if (loading) {
      return this.renderSpinner();
    }

    if (selectedQuestion) {
      return this.renderAnswer();
    }

    if (searchOpen && search.trim().length) {
      return this.renderSearch();
    }

    return this.currentNode() ? this.renderNode() : this.renderAreas();
  }

  render() {
    const { loading, path, selectedQuestion, search, searchOpen } = this.state;
    const { onClose } = this.props;
    /* Com uma área só, voltar da área levaria à lista com essa mesma área. */
    const nivelMinimo = this.hasManyAreas() ? 0 : 1;
    const podeVoltar = Boolean(selectedQuestion) || path.length > nivelMinimo;
    const buscando = searchOpen && search.trim().length > 0;

    return (
      <div className="faq-chat-widget">
        <div className="faq-chat-widget__header">
          <div className="faq-chat-widget__header-title">
            <strong>Suporte AVA</strong>
            <span>Perguntas frequentes</span>
          </div>
          <div className="faq-chat-widget__header-actions">
            {/* Sempre renderizado, apenas desabilitado quando não há para
                onde voltar: se ele aparecesse e desaparecesse, os outros
                botões pulariam de lugar embaixo do cursor. */}
            <button
              type="button"
              className="faq-chat-widget__icon-btn"
              title="Voltar"
              aria-label="Voltar"
              disabled={!podeVoltar || buscando}
              onClick={() => this.goBack()}
            >
              <RiArrowLeftLine size={18} />
            </button>
            <button
              type="button"
              className="faq-chat-widget__icon-btn"
              title={searchOpen ? "Fechar busca" : "Buscar uma dúvida"}
              aria-label={searchOpen ? "Fechar busca" : "Buscar uma dúvida"}
              onClick={() => this.toggleSearch()}
            >
              <RiSearchLine size={17} />
            </button>
            <button
              type="button"
              className="faq-chat-widget__icon-btn"
              title="Reiniciar conversa"
              aria-label="Reiniciar conversa"
              onClick={() => this.loadTree()}
            >
              <RiRefreshLine size={18} />
            </button>
            <button
              type="button"
              className="faq-chat-widget__icon-btn"
              title="Fechar"
              aria-label="Fechar"
              onClick={() => onClose()}
            >
              <RiCloseFill size={20} />
            </button>
          </div>
        </div>

        {!loading && searchOpen && (
          <div className="faq-chat-widget__search">
            <RiSearchLine size={15} className="faq-chat-widget__search-icon" />
            <input
              type="text"
              value={search}
              placeholder="Buscar uma dúvida..."
              onChange={e => this.setState({ search: e.target.value })}
            />
            {search.length > 0 && (
              <button
                type="button"
                className="faq-chat-widget__search-clear"
                title="Limpar busca"
                onClick={() => this.setState({ search: "" })}
              >
                <RiCloseFill size={15} />
              </button>
            )}
          </div>
        )}

        {!loading && !buscando && this.renderBreadcrumb()}

        <div className="faq-chat-widget__body">{this.renderBody()}</div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  general: state.general
});

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ showHelpdesk }, dispatch);
}

const FaqChatWidgetConectado = connect(
  mapStateToProps,
  mapDispatchToProps
)(FaqChatWidget);

/* O widget é montado no Main, acima de todas as telas. Sem esta barreira, um
   erro aqui dentro derrubaria a árvore React inteira e deixaria a plataforma
   inutilizável — que é exatamente o tipo de falha que não pode acontecer por
   causa de uma tela de ajuda. Com ela, o chat some sozinho e o resto do AVA
   segue funcionando. */
class FaqChatWidgetBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { falhou: false };
  }

  static getDerivedStateFromError() {
    return { falhou: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error("[FaqChatWidget] desativado por erro:", error);
  }

  render() {
    if (this.state.falhou) {
      return null;
    }

    return <FaqChatWidgetConectado {...this.props} />;
  }
}

export default FaqChatWidgetBoundary;
